import {Box, Grid, List, ListItem, ListItemIcon, Typography} from '@mui/material';
import {Circle} from '@mui/icons-material';
import moment from 'moment';
import {uniqBy} from 'lodash-es';
import parseHTML from 'html-react-parser';
import React, {useState} from 'react';
import {Button} from '../../components/Button/Button';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {RichEditor} from '../../components/RichEditor/RichEditor';
import {FormReadOnly} from '../../components/AutoForm/AutoFormNew';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {RiskType} from '../../schema-types/globalTypes';
import {
  GetRiskNotes,
  GetRiskNotes_reviewDetails_riskProfiles_previousSections,
} from '../../schema-types/GetRiskNotes';
import {UPDATES} from '../../shared/UPDATES';
import {
  riskNotesFormSectionNote,
  riskNotesFormSectionType,
  UpsertRiskProfileSectionsOptions,
} from './riskNotesFields';
import {riskTypeToDisplays} from './riskNotesText';
import {RiskNotesSideMenu} from './RiskNotesSideMenu';

const getPreviousSections = (
  risk: GetRiskNotes,
  riskType: RiskType
): GetRiskNotes_reviewDetails_riskProfiles_previousSections[] => {
  let previousSections: GetRiskNotes_reviewDetails_riskProfiles_previousSections[] = [];
  risk.reviewDetails?.riskProfiles.forEach((rp) => {
    previousSections = previousSections.concat(rp.previousSections);
  });
  return previousSections.filter(
    (ps) => ps.riskType === riskType && (ps.notes?.length ?? 0) > 0
  );
};

type onSectionChangeType = (
  riskType: RiskType,
  val: riskNotesFormSectionType,
  options?: UpsertRiskProfileSectionsOptions
) => void;

type RiskNotesEditorProps = {
  risk: GetRiskNotes;
  print: boolean;
  readOnly?: boolean;
  riskTypes: readonly RiskType[];
  onSectionChange: onSectionChangeType;
};

export const RiskNotesEditor: React.FC<RiskNotesEditorProps> = ({
  risk,
  print,
  readOnly = false,
  riskTypes,
  onSectionChange,
}) => {
  const [currentRiskType, setCurrentRiskType] = useState<RiskType>(riskTypes[0]);

  if (print) {
    return (
      <FormReadOnly readOnly>
        <Box display="flex" flexDirection="column" gap={4}>
          {riskTypes.map((riskType) => (
            <RiskSectionMenuItem
              key={riskType}
              riskType={riskType}
              risk={risk}
              print
              onSectionChange={onSectionChange}
            />
          ))}
        </Box>
      </FormReadOnly>
    );
  }

  return (
    <Box p={1} pl={0} pr={0}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <RiskNotesSideMenu
            riskTypes={riskTypes}
            currentRiskType={currentRiskType}
            setCurrentRiskType={setCurrentRiskType}
            readOnly={readOnly}
            onToggleNoChange={(riskType, section) =>
              onSectionChange(riskType, section, {skipRefetch: true})
            }
          />
        </Grid>
        <Grid item xs={9} sx={{position: 'relative'}}>
          <RiskSectionMenuItem
            key={currentRiskType}
            riskType={currentRiskType}
            risk={risk}
            print={false}
            onSectionChange={onSectionChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const RiskSectionMenuItem: React.FC<{
  riskType: RiskType;
  risk: GetRiskNotes;
  print: boolean;
  onSectionChange: onSectionChangeType;
}> = ({riskType, risk, print, onSectionChange}) => {
  const [helperOpen, setHelperOpen] = useState(false);
  const [{value: sectionValue}, {error: sectionError}] = useAutoField<
    riskNotesFormSectionType | undefined
  >(`${riskType}`);
  const [, {error: notesError}, {setValue: setNoteValue}] = useAutoField<
    string | undefined
  >(`${riskType}.notes`);
  const editorError =
    notesError ?? (typeof sectionError === 'string' ? sectionError : undefined);

  const display = riskTypeToDisplays[riskType];
  const showHelperIcon = Boolean(display.HelperText) && !print;
  const field = riskNotesFormSectionNote(riskType);
  const previousSections = getPreviousSections(risk, riskType);
  const disabled = sectionValue?.noChange === true;

  const prompts = (
    <Box>
      <Typography variant="h6">Prompts:</Typography>
      <List dense>
        {display.Prompts.map((prompt) => (
          <ListItem key={prompt}>
            <ListItemIcon sx={{width: 20, minWidth: 20}}>
              <Circle sx={{fontSize: 6}} />
            </ListItemIcon>
            <Typography variant="body2">{prompt}</Typography>
          </ListItem>
        ))}
      </List>
      {showHelperIcon && (
        <Box mt={2}>
          <Button
            eventName="risk-notes-further-guidance"
            variant="contained"
            color="secondary"
            onClick={() => setHelperOpen(true)}
          >
            Further Guidance
          </Button>
        </Box>
      )}
    </Box>
  );

  const history = (
    <Box
      mt={2}
      sx={
        print
          ? {}
          : {
              overflow: 'auto',
              maxHeight: '200px',
              display: 'flex',
              flexDirection: 'column',
            }
      }
    >
      <Typography variant="h6">Previous {display.title} notes:</Typography>
      {uniqBy(previousSections, 'notes').map((ps) => (
        <div key={ps.createdAt || ps.notes}>
          <Typography variant="caption">
            {moment(ps.createdAt).format('MMMM DD, YYYY')}
          </Typography>
          <Box>{parseHTML(ps.notes || '')}</Box>
        </div>
      ))}
      {!previousSections.length && <Typography>N/A</Typography>}
    </Box>
  );

  const editor = (
    <RichEditor
      formikId={`${riskType}.notes`}
      error={editorError}
      placeholder={field.placeholder || 'Enter notes'}
      initialValue={sectionValue?.notes}
      disabled={disabled}
      onSave={async (val) => {
        setNoteValue(UPDATES._1001, val, false);
        onSectionChange(riskType, {
          riskType,
          notes: val,
          noChange: Boolean(sectionValue?.noChange),
        });
      }}
    />
  );

  return (
    <Box data-segment={riskType} sx={print ? undefined : {minHeight: 600}}>
      <Box mb={2}>
        <Typography variant="h4" color="primary">
          <strong>{display.title}</strong>
        </Typography>
      </Box>
      {display.HelperText && (
        <ScrollableModal
          open={helperOpen}
          onClose={() => setHelperOpen(false)}
          size="medium"
          autoHeight
        >
          <Box>
            <Typography variant="h5" mb={2}>
              {display.title}
            </Typography>
            <List>
              {display.HelperText.map((line) => (
                <ListItem key={line} alignItems="flex-start" sx={{py: 0.75}}>
                  <ListItemIcon sx={{width: 20, minWidth: 20, mt: 1.25}}>
                    <Circle sx={{fontSize: 6}} />
                  </ListItemIcon>
                  <Typography variant="body1" sx={{lineHeight: 1.6}}>
                    {line}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </ScrollableModal>
      )}
      {print ? (
        <>
          {editor}
          <Box mt={2}>{prompts}</Box>
          {history}
        </>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={8}>
            {editor}
          </Grid>
          <Grid item xs={4}>
            {prompts}
          </Grid>
          <Grid item xs={12}>
            {history}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
